import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";

import { monospaceStyle } from "../../theme";
import { ProductStandard } from "../../types/productPlane";
import SourcesChip from "../Detail/SourcesChip";
import Tag from "../Detail/Tag";
import { columnHeader, quiet, tableFrame } from "../Detail/claimCells";
import { codeWords } from "../Detail/format";

interface ProductStandardsProps {
  standards: ProductStandard[];
  // A plain table for a printed page: no frame, no sources column
  print?: boolean;
}

// A standard the product is only claimed to meet is set apart
const STATUS_IN_WARNING = "claimed";

const COLUMNS = ["Standard", "Body", "Status", "Sources"];

/**
 * The standards of a product as a table, in the frame of every table of
 * the view: each as its designation, the body that issues it, how it is
 * claimed, and its sources chip.
 */
const ProductStandards: React.FC<ProductStandardsProps> = ({
  standards,
  print = false,
}) => {
  if (standards.length === 0) {
    return null;
  }
  const columns = print ? COLUMNS.slice(0, -1) : COLUMNS;

  return (
    <TableContainer sx={print ? undefined : tableFrame}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column}
                align={column === "Sources" ? "right" : "left"}
                sx={columnHeader}
              >
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {standards.map((standard, index) => (
            <TableRow key={index}>
              <TableCell>
                <Box component="span" sx={monospaceStyle}>
                  {standard.standard}
                </Box>
              </TableCell>
              <TableCell sx={quiet}>{standard.body}</TableCell>
              <TableCell>
                {standard.status && (
                  <Box component="span" title={standard.status_wording ?? undefined}>
                    <Tag
                      tone={
                        standard.status === STATUS_IN_WARNING ? "warning" : "default"
                      }
                    >
                      {codeWords(standard.status)}
                    </Tag>
                  </Box>
                )}
              </TableCell>
              {!print && (
                <TableCell align="right">
                  <SourcesChip sources={standard.sources} />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProductStandards;
